// Preact
// eslint-disable-next-line no-unused-vars
import { h } from 'preact';
import classNames from 'classnames';
import { useContext } from 'preact/hooks';
import { SearchContext } from '../contexts/Search';

const { __ } = wp.i18n;

/**
 * The main component.
 *
 * @return {Object} JSX
 */
const CurrentSelections = () => {
	const { state, helpers } = useContext( SearchContext );
	const { update } = helpers;
	const { currentSelections, params, filterQueries, loading } = state;

	// No selections? Bail.
	if ( !currentSelections.length && !filterQueries.length ) {
		return null;
	}

	const notDisabledSelections = currentSelections.filter( ( selection ) => {
		if ( !selection.default ) {
			return selection;
		}
	} );

	/**
	 * Handle button click.
	 * Should update the params.
	 *
	 * @param {string} name The param/key of the current selection.
	 * @param {string} value The value of the current selection.
	 */
	const handleOnClick = ( name, value ) => {
		const updatedParams = { ...params };

		// Clean filter query name.
		if ( 'filter_queries[]' in updatedParams ) {
			updatedParams['filter_queries'] = updatedParams['filter_queries[]'];
			delete updatedParams['filter_queries[]'];
		}

		const key = updatedParams[name];

		if ( 'filter_queries' === name && Array.isArray( key ) ) {
			// Remove specified filter query.
			updatedParams[ name ] = key.filter( ( filterQuery ) => filterQuery !== value );
		} else if ( 'facet[date][fixed]' === name && 'custom-range' === value ) {
			delete updatedParams[name];
			delete updatedParams['facet[date][range][from]'];
			delete updatedParams['facet[date][range][to]'];
		} else if ( Array.isArray( key ) ) {
			// Remove the specific value from the array of values.
			const index = key.indexOf( value );
			key.splice( index, 1 );
		} else {
			delete updatedParams[name];
		}

		// Reset the page.
		updatedParams.page = 1;

		update( updatedParams );
	};

	/**
	 * Handle clear on click.
	 */
	const handleClearOnClick = () => {
		update( { q: params.q } );
	};

	// Render the component.
	// TODO this is the JS showing the current selections. Need to add locale in here.
	return (
		<div className={classNames( 'current-selections', { 'current-selections--is-loading': loading } )}>
			<h2 className="current-selections__heading body small font-italic text-neutral-400">
				{__( 'Current Selections', 'ms-research' )}
			</h2>
			<ul className="current-selections__list d-flex flex-column list-unstyled mb-2">
				{currentSelections.map( ( { label, name, value, parent, default: disabled }, index ) => {

					return (
						<li className="current-selections__list-item mb-2" key={`${value}-${index}`}>
							<button
								disabled={disabled}
								type="button"
								className="btn btn-sm m-0 glyph-append glyph-append-cancel text-inverse current-selections__selection"
								aria-label={`Remove ${parent.label}: ${label} filter`}
								data-bi-type="button"
								data-bi-bhvr="5"
								data-bi-tN="faceted-search/sidebar-current"
								onClick={() => {
									handleOnClick( name, value );
								}}
							>
								{parent.label}: {label}
							</button>
						</li>
					);
				} )}
				{filterQueries.map( ( value, index ) => {
					return (
						<li className="current-selections__list-item mb-2" key={`${value}-${index}`}>
							<button
								type="button"
								className="btn btn-sm m-0 glyph-append glyph-append-cancel current-selections__selection"
								aria-label={`Remove Filter: ${value}`}
								data-bi-type="button"
								data-bi-tN="faceted-search/sidebar-current"
								data-bi-bhvr="5"
								onClick={() => {
									handleOnClick( 'filter_queries', value );
								}}
							>
								{__( 'Filter: %s', 'ms-research' ).replace( '%s', value )}
							</button>
						</li>
					);
				} )}
			</ul>
			{notDisabledSelections.length ? (
				<button
					type="button"
					className="btn btn-sm btn-link  font-weight-normal py-1 px-0 text-decoration-none mb-3"
					data-bi-bhvr="5"
					onClick={handleClearOnClick}
				>
					<span className="glyph-prepend glyph-prepend-xsmall glyph-prepend-cancel mr-1 d-inline-flex" />
					{__( 'Clear selections', 'ms-research' )}
				</button>
			) : null}
		</div>
	);
};

export default CurrentSelections;
